import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import Breadcrumb from '@/components/layout/Breadcrumb'
import Card from '@/components/ui/Card'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import doubtResponseApiService, { DoubtResponse } from '@/services/doubtResponseApiService'
import { getLocalizedValue } from '@sakeena/shared'

const normalizeResponse = (response: DoubtResponse): DoubtResponse => {
  return {
    id: response.id || response.Id,
    Id: response.Id || response.id,
    question: response.question || response.Question,
    Question: response.Question || response.question,
    answer: response.answer || response.Answer,
    Answer: response.Answer || response.answer,
    imageUrl: response.imageUrl || response.ImageUrl,
    ImageUrl: response.ImageUrl || response.imageUrl,
    videoUrl: response.videoUrl || response.VideoUrl,
    VideoUrl: response.VideoUrl || response.videoUrl,
    externalVideoLink: response.externalVideoLink || response.ExternalVideoLink,
    ExternalVideoLink: response.ExternalVideoLink || response.externalVideoLink,
    order: response.order ?? response.Order ?? 0,
    Order: response.Order ?? response.order ?? 0,
    isActive: response.isActive ?? response.IsActive ?? true,
    IsActive: response.IsActive ?? response.isActive ?? true,
  }
}

export default function DoubtResponsesPage() {
  const { t, i18n } = useTranslation()
  const safeT = t || ((key: string, def: string) => def || key)
  const currentLang = i18n?.language || 'ar'
  const [responses, setResponses] = useState<DoubtResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedResponse, setSelectedResponse] = useState<DoubtResponse | null>(null)

  useEffect(() => {
    const loadResponses = async () => {
      try {
        setLoading(true)
        const data = await doubtResponseApiService.getActiveDoubtResponses()
        const normalized = data.map(normalizeResponse)
        setResponses(normalized.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)))
        if (normalized.length > 0) {
          setSelectedResponse(normalized[0])
        }
      } catch (err) {
        console.error('Failed to load doubt responses:', err)
        setError(safeT('doubtResponses.loadingError', 'فشل تحميل الردود على الشبهات'))
      } finally {
        setLoading(false)
      }
    }

    loadResponses()
  }, [safeT, currentLang])

  const breadcrumbItems = [
    { label: safeT('common.home', 'الرئيسية'), to: '/' },
    { label: safeT('doubtResponses.nav', 'الرد على الشبهات') },
  ]

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{safeT('doubtResponses.title', 'الرد على الشبهات')} - {safeT('brand.name', 'سكينة')}</title>
        </Helmet>
        <div className="min-h-screen bg-[#f5fbf9] dark:bg-gray-900">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-center min-h-[60vh]">
            <LoadingSpinner />
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{safeT('doubtResponses.title', 'الرد على الشبهات')} - {safeT('brand.name', 'سكينة')}</title>
        </Helmet>
        <div className="min-h-screen bg-[#f5fbf9] dark:bg-gray-900">
          <Breadcrumb items={breadcrumbItems} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <Card className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </Card>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{safeT('doubtResponses.title', 'الرد على الشبهات')} - {safeT('brand.name', 'سكينة')}</title>
      </Helmet>
      <div className="min-h-screen bg-[#f5fbf9] dark:bg-gray-900">
        <Breadcrumb items={breadcrumbItems} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">{safeT('doubtResponses.title', 'الرد على الشبهات')}</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">{safeT('doubtResponses.subtitle', 'إجابات واضحة على الأسئلة والشبهات الشائعة')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar with questions list */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-4">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{safeT('doubtResponses.questions', 'الأسئلة')}</h2>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {responses.map((response) => {
                    const normalized = normalizeResponse(response)
                    return (
                      <button
                        key={normalized.Id || normalized.id}
                        onClick={() => setSelectedResponse(normalized)}
                        className={`w-full text-right p-4 rounded-lg transition-colors ${
                          selectedResponse?.Id === normalized.Id ||
                          selectedResponse?.id === normalized.id
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100'
                            : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <p className="font-medium">{getLocalizedValue(normalized.Question, currentLang)}</p>
                      </button>
                    )
                  })}
                </div>
              </Card>
            </div>

            {/* Main content area */}
            <div className="lg:col-span-2">
              {selectedResponse ? (
                <Card className="p-8">
                  <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100">
                    {getLocalizedValue(selectedResponse.Question, currentLang)}
                  </h2>

                  {selectedResponse.ImageUrl && (
                    <div className="mb-6 rounded-lg overflow-hidden">
                      <img
                        src={selectedResponse.ImageUrl}
                        alt={getLocalizedValue(selectedResponse.Question, currentLang)}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  )}

                  {selectedResponse.VideoUrl && (
                    <div className="mb-6">
                      <video
                        src={selectedResponse.VideoUrl}
                        controls
                        className="w-full rounded-lg"
                      >
                        {safeT('common.videoNotSupported', 'متصفحك لا يدعم تشغيل الفيديو.')}
                      </video>
                    </div>
                  )}

                  {selectedResponse.ExternalVideoLink && (
                    <div className="mb-6">
                      <div className="aspect-video rounded-lg overflow-hidden">
                        <iframe
                          src={selectedResponse.ExternalVideoLink}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className="prose prose-lg max-w-none dark:prose-invert text-gray-700 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: getLocalizedValue(selectedResponse.Answer, currentLang) || '',
                    }}
                  />
                </Card>
              ) : (
                <Card className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">{safeT('doubtResponses.selectQuestion', 'اختر سؤالاً من القائمة لعرض الإجابة')}</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

